import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import SearchField from 'components/Form/SearchField/SearchField'
import { addToast } from 'components/Toast/Toast'
import { useHeaderNav } from 'context/headerNavContext'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import { useDebouncedValue } from 'helpers/hooks/useDebouncedValue/useDebouncedValue'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { useUserProfile } from 'store'

import { Faq } from './components/Faq/Faq'
import { Tutorials } from './components/Tutorials/Tutorials'
import { useGetCategories } from './service/react-query/useGetCategories/useGetCategories'
import { useGetSearchArticle } from './service/react-query/useGetSearchArticle/useGetSearchArticle'
import { useFilterAndSearchStore } from './store/filterAndSearch'
import { type IArticleSearchItem } from './types/interfaces/IHowTo'

export const HowTo = () => {
  const { setTitle } = usePageTitle()
  const { setHeaderNav } = useHeaderNav()
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()

  const { data: categories } = useGetCategories()
  const userData = useUserProfile()
  const changeFilter = useFilterAndSearchStore(state => state.changeFilter)
  const searchTerm = useFilterAndSearchStore(state => state.searchTerm)
  const setSearchTerm = useFilterAndSearchStore(state => state.setSearchTerm)

  const [searchArticles, setSearchArticles] =
    useState<IArticleSearchItem[]>()

  const searchCategory =
    id === 'tutorials' || id === 'faq'
      ? id
      : undefined

  const debouncedSearchTerm = useDebouncedValue<string | undefined>({
    initialValue: searchTerm.query,
    delay: 400
  })

  const { mutate } = useGetSearchArticle()

  const faqCategory = useMemo(() => {
    return categories?.message?.find(category => {
      const categoryName = category.name.toLowerCase().trim()

      return categoryName === 'faq' || categoryName.startsWith('faq ')
    })
  }, [categories])

  const tutorialsCategory = useMemo(() => {
    return categories?.message?.find(category => {
      const categoryName = category.name.toLowerCase().trim()

      return (
        categoryName === 'tutorials' ||
        categoryName.startsWith('tutorials ')
      )
    })
  }, [categories])

  useEffect(() => {
    setTitle(userData?.role === 'fan' ? 'FAQ' : t('howTo'))
  }, [setTitle, t, userData?.role])

  useEffect(() => {
    setHeaderNav(['/how-to/faq', '/how-to/tutorials'])

    return () => {
      setHeaderNav([])
    }
  }, [setHeaderNav])

  useEffect(() => {
    if (!debouncedSearchTerm || !searchCategory) {
      setSearchArticles(undefined)
      return
    }

    mutate(
      {
        query: debouncedSearchTerm,
        category: searchCategory
      },
      {
        onSuccess: data => {
          setSearchArticles(data.data.message)
        },
        onError: error => {
          if (error instanceof Error) {
            addToast(
              'error',
              error.message || t('error:errorSomethingWentWrong')
            )
          }
        }
      }
    )
  }, [debouncedSearchTerm, mutate, searchCategory, t])

  const handleSearchChange = (term: string) => {
    if (!searchCategory) {
      return
    }

    setSearchTerm({
      query: term,
      category: searchCategory
    })

    changeFilter(null)
  }

  const handleSearchClear = () => {
    setSearchTerm({
      query: '',
      category: searchCategory ?? ''
    })

    setSearchArticles(undefined)
    changeFilter(null)
  }

  const renderPage = () => {
    switch (id) {
      case 'tutorials':
        if (!tutorialsCategory) {
          return null
        }

        return (
          <Tutorials
            id={tutorialsCategory.id}
            searchArticles={searchArticles}
          />
        )

      case 'faq':
        if (!faqCategory) {
          return null
        }

        return (
          <Faq
            id={faqCategory.id}
            // searchArticles={searchArticles}
          />
        )

      default:
        return <Navigate to='/404' replace />
    }
  }

  const renderHeader = () => {
    switch (id) {
      case 'tutorials':
        return (
          <SearchField
            value={searchTerm.query}
            changeFn={handleSearchChange}
            clearFn={handleSearchClear}
            additionalProps={{
              placeholder: t('searchTutorials')
            }}
          />
        )

      case 'faq':
        return (
          <SearchField
            value={searchTerm.query}
            changeFn={handleSearchChange}
            clearFn={handleSearchClear}
            additionalProps={{
              placeholder: t('searchQuestions')
            }}
          />
        )

      default:
        return null
    }
  }

  return (
    <div className='how-to-container'>
      <WithHeaderSection headerSection={renderHeader()}>
        {renderPage()}
      </WithHeaderSection>

      <DesktopAdditionalContent />
    </div>
  )
}